import { useCallback, useEffect, useState, type FC } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'

import { Text } from 'components'

import styles from './Exhibitions.module.scss'

type TExhibition = {
  id: string
  name: string
  date: string
  location: string
  website?: string
  images: string[]
}

const base = `${process.env.PUBLIC_URL || ''}/exhibitions`

const exhibitions: TExhibition[] = [
  {
    id: 'big5',
    name: 'exhibitionBig5Name',
    date: 'exhibitionBig5Date',
    location: 'exhibitionBig5Location',
    website: 'https://www.big5global.com/',
    images: [`${base}/big5-1.jpeg`, `${base}/big5-2.jpeg`, `${base}/big5-3.jpeg`, `${base}/big5-4.jpeg`],
  },
  {
    id: 'aquatherm',
    name: 'exhibitionAquathermName',
    date: 'exhibitionAquathermDate',
    location: 'exhibitionAquathermLocation',
    website: 'https://aquaflameexpo.com/',
    images: [
      `${base}/aquatherm-1.jpeg`,
      `${base}/aquatherm-2.jpeg`,
      `${base}/aquatherm-3.jpeg`,
      `${base}/aquatherm-4.jpeg`,
    ],
  },
  {
    id: 'isksodex',
    name: 'exhibitionIsksodexName',
    date: 'exhibitionIsksodexDate',
    location: 'exhibitionIsksodexLocation',
    images: [`${base}/isksodex-1.png`, `${base}/isksodex-2.png`],
  },
  {
    id: 'securika',
    name: 'exhibitionSecurikaName',
    date: 'exhibitionSecurikaDate',
    location: 'exhibitionSecurikaLocation',
    website: 'https://securikaexpo.com/',
    images: [`${base}/securika-1.jpeg`, `${base}/securika-2.jpeg`, `${base}/securika-3.jpeg`],
  },
]

const facts = [
  { label: 'exhibitionLabelType', value: 'exhibitionTypeExpo' },
  { label: 'exhibitionLabelSector', value: 'exhibitionSectorBuilding' },
  { label: 'exhibitionLabelAudience', value: 'exhibitionAudienceTrade' },
]

type TCarouselProps = {
  images: string[]
  label: string
  onOpen: (index: number) => void
}

const Carousel: FC<TCarouselProps> = ({ images, label, onOpen }) => {
  const [index, setIndex] = useState(0)
  const many = images.length > 1

  const prev = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIndex(i => (i - 1 + images.length) % images.length)
  }
  const next = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIndex(i => (i + 1) % images.length)
  }

  return (
    <div className={styles.carousel}>
      <div className={styles.carousel__viewport}>
        <img src={images[index]} alt='' className={styles.carousel__img} onClick={() => onOpen(index)} />
        {many && (
          <>
            <button
              type='button'
              className={classNames(styles.carousel__nav, styles.carousel__nav_prev)}
              onClick={prev}
              aria-label='Previous'
            >
              <span aria-hidden />
            </button>
            <button
              type='button'
              className={classNames(styles.carousel__nav, styles.carousel__nav_next)}
              onClick={next}
              aria-label='Next'
            >
              <span aria-hidden />
            </button>
          </>
        )}
      </div>
      {many && (
        <div className={styles.carousel__dots}>
          {images.map((src, d) => (
            <button
              type='button'
              key={src}
              className={classNames(styles.carousel__dot, { [styles.carousel__dot_active]: d === index })}
              onClick={() => setIndex(d)}
              aria-label={`${label} ${d + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

type TLightbox = { images: string[]; index: number }

const Exhibitions: FC = () => {
  const [lightbox, setLightbox] = useState<TLightbox | null>(null)

  const close = useCallback(() => setLightbox(null), [])
  const prev = useCallback(
    () => setLightbox(lb => (lb ? { ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length } : lb)),
    []
  )
  const next = useCallback(
    () => setLightbox(lb => (lb ? { ...lb, index: (lb.index + 1) % lb.images.length } : lb)),
    []
  )

  useEffect(() => {
    if (!lightbox) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }

    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [lightbox, close, prev, next])

  return (
    <section className={classNames('container', styles.exhibitions)}>
      <div className={styles.exhibitions__head}>
        <span className={styles.exhibitions__accent} aria-hidden />
        <Text text='exhibitionsTitle' className={styles.exhibitions__title} tagName='h2' />
        <Text text='exhibitionsSubtitle' className={styles.exhibitions__subtitle} />
      </div>

      <div className={styles.list}>
        {exhibitions.map(expo => (
          <article className={styles.expo} key={expo.id}>
            <Carousel
              images={expo.images}
              label={expo.id}
              onOpen={i => setLightbox({ images: expo.images, index: i })}
            />

            <div className={styles.expo__info}>
              <Text text={expo.name} className={styles.expo__name} tagName='h3' />

              <dl className={styles.expo__facts}>
                <div className={styles.expo__fact}>
                  <Text text='exhibitionLabelDate' className={styles.expo__factLabel} tagName='dt' />
                  <Text text={expo.date} className={styles.expo__factValue} tagName='dd' />
                </div>
                <div className={styles.expo__fact}>
                  <Text text='exhibitionLabelLocation' className={styles.expo__factLabel} tagName='dt' />
                  <Text text={expo.location} className={styles.expo__factValue} tagName='dd' />
                </div>
                {facts.map(f => (
                  <div className={styles.expo__fact} key={f.label}>
                    <Text text={f.label} className={styles.expo__factLabel} tagName='dt' />
                    <Text text={f.value} className={styles.expo__factValue} tagName='dd' />
                  </div>
                ))}
              </dl>

              {expo.website && (
                <a className={styles.expo__link} href={expo.website} target='_blank' rel='noopener noreferrer'>
                  <Text text='visitWebsite' emptyTag />
                  <span className={styles.expo__link_icon} aria-hidden />
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      {lightbox &&
        createPortal(
          <div className={styles.lightbox} onClick={close} role='dialog' aria-modal='true'>
            <button className={styles.lightbox__close} onClick={close} aria-label='Close'>
              <span aria-hidden />
            </button>
            {lightbox.images.length > 1 && (
              <button
                className={classNames(styles.lightbox__nav, styles.lightbox__nav_prev)}
                onClick={e => {
                  e.stopPropagation()
                  prev()
                }}
                aria-label='Previous'
              >
                <span aria-hidden />
              </button>
            )}
            <img
              src={lightbox.images[lightbox.index]}
              alt=''
              className={styles.lightbox__image}
              onClick={e => e.stopPropagation()}
            />
            {lightbox.images.length > 1 && (
              <button
                className={classNames(styles.lightbox__nav, styles.lightbox__nav_next)}
                onClick={e => {
                  e.stopPropagation()
                  next()
                }}
                aria-label='Next'
              >
                <span aria-hidden />
              </button>
            )}
            <span className={styles.lightbox__counter}>
              {lightbox.index + 1} / {lightbox.images.length}
            </span>
          </div>,
          document.body
        )}
    </section>
  )
}

export default Exhibitions
